/**
 * Manages observation of image elements for resizing and intersection changes,
 * to adjust CSS variables and support lazy loading of images.
 */
class ImageObserverManager {

  constructor(app, imageBox, cssVar, container = false) {
    this.app = app; // Reference to the app object that includes cssVar, createSvgObject, and loadSvg methods
    this.container = container;
    this.imageBox = imageBox;
    this.cssVar = cssVar;
    this.initObservers();
  }

  // Method to set the calendar card width
  setCardWidth() {
    let imageBox;
    if (this.container) {
      imageBox = $(this.imageBox, this.container).eq(0);
    } else {
      imageBox = this.imageBox;
    }

    const width = $(imageBox).width();
    if (width > 0) {
      this.app.cssVar(`${this.cssVar}`, width, 'px');
    }
  }

  // Method to initialize both ResizeObserver and IntersectionObserver
  initObservers() {
    this.resizeObserver = new ResizeObserver(entries => {
      if (this.cssVar) {
        this.setCardWidth(); // Call setCardWidth on resize events
      }
    });

    this.intersectionObserver = new IntersectionObserver((entries, self) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Process for lazy loading images
          let $target = $(entry.target);
          if($target.is('[data-type]') && $target.attr('data-type') === 'iframe') {
            const blobUrl = $target.attr('data-src');
            $target.replaceWith(`<iframe src="${blobUrl}" width="100%"  frameborder="0" scrolling="auto"></iframe>`);
          } else {
            let $object = this.app.createSvgObject($target);
            this.app.loadSvg($object);
          }
          self.unobserve(entry.target); // Stop observing the now loaded image
        }
      });
    }, {threshold: 0});

    this.pageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const element = entry.target;
              if (typeof this.callback === 'function') {
                this.callback(element);
              }
            }
          });
        },
        {
          threshold: [0, 0.5, 1] // callback triggers when target starts to become visible, is halfway visible, and fully visible
        }
    );
  }

  // Method to start observing an element for resize
  observeResize(element) {
    this.resizeObserver.observe(element);
  }

  observePageEnd(element, callback) {
    this.callback = callback;
    this.pageObserver.observe(element);
  }

  unobservePageEnd(element, callback) {
    this.pageObserver.unobserve(element);
  }

  // Method to start observing an element for intersection
  observeIntersection(element) {
    this.intersectionObserver.observe(element);
  }
}

class BaseClass {

  constructor(app) {
    this.app = app;
    this.isRendering = true;
  }

  onRenderList() {
    const self = this;
    // Return a promise that resolves when the value changes
    return new Promise((resolve) => {
      // This function simulates the value change
      function checkValue() {
        // Simulate a condition check, e.g., value change
        if (self.isRendering === false) {
          resolve(self);
        } else {
          // Re-check the value every 100ms
          setTimeout(checkValue, 100);
        }
      }

      // Start checking the value
      checkValue();
    });
  }

}

/**
 * Abstract class for creating and managing a searchable table list.
 * This class provides basic functionalities like showing a loader and initializing search.
 */
class TableList {

  showTab(id) {
    let triggerEl = $(`.pg-header-wth-tab a[href="${id}"]`)
    if (!triggerEl.length) {
      triggerEl = $(`.sub-menu a[href="${id}"]`)
    }
    if (!triggerEl.length) {
      triggerEl = $(`.social-networks-profiles a[href="${id}"]`)
    }

    if (triggerEl.length) {
      const tab = new bootstrap.Tab(triggerEl);
      tab.show() // Select tab by name
    }
  }

  getTagClass() {
    return Utils.getTagClass();
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  onRenderList() {
    const self = this;
    // Return a promise that resolves when the value changes
    return new Promise((resolve) => {
      // This function simulates the value change
      function checkValue() {
        // Simulate a condition check, e.g., value change
        if (self.isRendering === false) {
          resolve(self);
        } else {
          // Re-check the value every 100ms
          setTimeout(checkValue, 100);
        }
      }

      // Start checking the value
      checkValue();
    });
  }

  staticPH = '<p class="placeholder-glow"><span class="placeholder col-8"></span></p>';

  constructor(app, container, listRow, searchInputId = 'searchTable') {
    if (new.target === TableList) {
      throw new Error("TableList is an abstract class and cannot be instantiated directly.");
    }
    this.app = app;
    this.container = container;
    this.isRendering = true;
    this.listRow = listRow;
    this.userTimeZone = app.apis['user'].get()['timezone'];
    if (searchInputId) {
      this.initSearch(this.container, searchInputId);
    }
  }

  getLoaderRow(row) {
    const page = _.template(this.app.templates[this.listRow]);
    let $row = $(page(row)).addClass('pre-loader');
    const $newRow = $row.clone();
    $('.as-placeholder-glow', $newRow).removeClass('as-placeholder-glow').addClass('placeholder-glow');
    $('.as-placeholder', $newRow).removeClass('as-placeholder').addClass('placeholder');
    return $newRow;
  }

  showLoader(row, loaderRows = 8) {
    for (let i = 0; i < loaderRows; i++) {
      const $newRow = this.getLoaderRow(row);
      $newRow.appendTo(this.container);
    }
  }
  showLoaderInto(container, row, loaderRows = 8) {
    const page = _.template(this.app.templates[this.listRow]);
    let $row = $(page(row)).addClass('pre-loader');
    for (let i = 0; i < loaderRows; i++) {
      const $newRow = $row.clone();
      $('.as-placeholder-glow', $newRow).removeClass('as-placeholder-glow').addClass('placeholder-glow');
      $('.as-placeholder', $newRow).removeClass('as-placeholder').addClass('placeholder');
      $newRow.appendTo(container);
    }
  }

  removeLoader() {
    this.container.find('.pre-loader').remove();
  }

  clearLoader(container) {
    const $this = $(container);
    $this.removeClass('pre-loader');
    $('.placeholder-glow', $this).remove();

    container.find('.pre-loader').each(function () {
      const $this = $(this);
      $this.removeClass('pre-loader');
      $('.placeholder-glow', $this).remove();
    })
  }

  renderList() {
    throw new Error("renderList() must be implemented by the subclass");
  }

  // Common utility method which can be used by subclasses
  initSearch(listContainerSelector, searchInputId) {
    const $searchInput = $(`#${searchInputId}`);
    const $listContainer = $(listContainerSelector);

    $searchInput.on('keyup', function () {
      const searchQuery = this.value.toLowerCase();
      let $tr = $('tr', $listContainer);

      $tr.each(function () {
        let $td = $(this).find('td').eq(0); // Assuming search is always in the first column
        if ($td.text().toLowerCase().indexOf(searchQuery) > -1) {
          $(this).removeClass('d-none');
        } else {
          $(this).addClass('d-none');
        }
      });
    });
  }

  /**
   * Abstract class for creating and managing a searchable table list.
   * This class provides basic functionalities like showing a loader and initializing search.
   */
}


class LazyLoader {
  constructor(app, container, loadMoreCallback, threshold = 0) {
    this.app = app;
    this.container = container;
    this.loadMoreCallback = loadMoreCallback;
    this.threshold = threshold;
    this.isLoading = false;
    this.hasMore = true; // Indicates if there are more items to load
    this.loaderId = this.app.generateId();
    this.observerElement = $(`<div id="${this.loaderId}" data-lazy-loader="${this.loaderId}"></div>`);
    this.lazyContainer = this.container.closest('.lazy-loader-container');
    if (this.lazyContainer.length === 0) {
      throw new Error(`Missing lazy load container on ${container}`);

    }
    // clean any old observer
    if (this.lazyContainer.find('[data-lazy-loader]').length) {
      this.lazyContainer.find('[data-lazy-loader]').remove();
    }

    this.observerElement.appendTo(this.lazyContainer);
    this.observerElement = this.observerElement[0];

    // Set up Intersection Observer
    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {threshold});
    this.observer.observe(this.observerElement);
  }

  destroy() {
    // clean any old observer
    if (this.lazyContainer.find('[data-lazy-loader]').length) {
      this.lazyContainer.find('[data-lazy-loader]').remove();
    }
  }

  handleIntersect(entries) {
    const entry = entries[0];

    //console.log(entry.isIntersecting, this.hasMore);
    if (!entry.isIntersecting || !this.hasMore) return;

    this.isLoading = true;
    this.loadMore().then(hasMore => {
      this.isLoading = false;
      this.hasMore = hasMore;

      // If there are more items, observe the last item again
      if (!this.hasMore) {
        // No more items, stop observing
        this.observer.unobserve(this.observerElement);
      }
    });
  }

  async loadMore() {
    const hasMore = await this.loadMoreCallback();
    return hasMore;
  }
}


class NoRecordsPlaceholder {
  templateName = 'no-records__global'
  defaults = {'addButton': false, 'help': false, 'class': ''}

  constructor(app, container, options, templateName) {
    this.app = app;
    this.$container = container;
    this.options = {...this.defaults, ...options};
    if (templateName) {
      this.templateName = templateName;
    }
    this.template = _.template(this.app.templates[this.templateName]);
  }

  static destroy() {
    $('.no-records-placeholder').remove();
  }

  render(options = {'class' : 'no-records-placeholder' }) {
    // Create the main placeholder div
    this.$placeholderDiv = $('<div></div>', {
      class: options.class
    });

    this.$placeholderDiv.addClass(this.options.class);
    this.$container.addClass(`${this.options.class}-container`);

    const $placeholder = $(
        this.template(this.options)
    );

    $placeholder.appendTo(this.$placeholderDiv);

    // Add Button
    if (this.options.addButton) {
      const $addButton = $('.btn-add-new', $placeholder);
      $addButton.on('click', this.options.addButton.onClick);
    }

    // Append the placeholder to the container
    this.$container.append(this.$placeholderDiv);
  }
}

class Toast {
  templateName = 'global__toast'
  defaults = {'closeButton': false, 'toastClass' : ' m-5 mb-0'}

  constructor(app, container, options) {
    this.app = app;
    this.$container = container;
    this.options = {...this.defaults, ...options};
    this.template = _.template(this.app.templates[this.templateName]);
  }

  static destroy() {
    const filterToast = document.querySelector('.toast');
    if(filterToast) {
      const toastBootstrap = bootstrap.Toast.getOrCreateInstance(filterToast);
      toastBootstrap.hide();
    }
  }

  render() {
    const $placeholder = $(
        this.template(this.options)
    );

    // Append the placeholder to the container
    this.$container.prepend($placeholder);

    const filterToast = document.querySelector('.toast');
    const toastBootstrap = bootstrap.Toast.getOrCreateInstance(filterToast);
    toastBootstrap.show();

    // Add Button
    if (this.options.closeButton) {
      const $addButton = $('.btn-close-toast', $placeholder);
      $addButton.on('click', this.options.closeButton.onClick);
    }
  }
}

class MassActions {
  templateName = 'global__mass-actions'
  defaults = {'class' : 'mass-actions-placeholder', 'deleteButton': false, separator : true}

  constructor(app, container, options) {
    this.app = app;
    this.$container = container;
    this.options = {...this.defaults, ...options};
    this.template = _.template(this.app.templates[this.templateName]);
  }

  static destroy() {
    $('.mass-actions-placeholder').remove();
  }

  render() {
    // Create the main placeholder div
    this.$placeholderDiv = $('<div></div>', {
      class: this.options.class
    });

    this.$placeholderDiv.addClass(this.options.class);
    this.$container.addClass(`${this.options.class}-container`);

    const $placeholder = $(
        this.template(this.options)
    );

    $placeholder.appendTo(this.$placeholderDiv);

    // Delete Button
    if (this.options.deleteButton) {
      const $addButton = $('.btn-mass-delete', $placeholder);
      $addButton.on('click', this.options.deleteButton.onClick);
    }

    // Tag Button
    if (this.options.tagButton) {
      const $addButton = $('.btn-mass-tag', $placeholder);
      $addButton.on('click', this.options.tagButton.onClick);
    }

    // Export Button
    if (this.options.exportButton) {
      const $exportButton = $('.btn-mass-export', $placeholder);
      $exportButton.on('click', this.options.exportButton.onClick);
    }

    // Campaign Button
    if (this.options.campaignButton) {
      const $campaignButton = $('.btn-mass-campaign', $placeholder);
      $campaignButton.on('click', this.options.campaignButton.onClick);
    }

    // Append the placeholder to the container
    this.$container.append(this.$placeholderDiv);
  }
}
